import { Fragment, useState, useMemo } from "react";
import { Download, Info, AlertTriangle, RotateCcw, Pencil, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { CostRowLabel } from "./CostRowInfo";
import { r2 } from "./costMath";
import { computeQuoteValidity, fmtDate, SYMBOLS } from "./format";
import { SectionHead } from "./shared";
import { isoCountryCode } from "../../lib/countryCodes";

export function CostsTab({
  d,
  money,
  targetCurrency,
  targetRate,
  setTargetCurrency,
  setTargetRate,
  adjusted,
  sampleOverrides,
  setSampleOverrides,
  unitOverrides,
  setUnitOverrides,
  editMode,
  setEditMode,
  exportUrl,
  pdfExportUrl,
  supplierOverlay = null,
  acknowledged,
  setAcknowledged,
  recordEvent,
}) {
  const isGBP = targetCurrency === "GBP";
  const pivots = d.phase_pivots || [];
  // Tab-open disclaimer: clients must acknowledge ONCE PER VISIT that
  // the costs are based on the specs they shared + our project
  // assumptions. The `acknowledged` flag is held by PublicProposal at
  // the page level so tab switches within a single visit don't
  // re-prompt, but on every fresh page load the parent resets it so
  // the disclaimer fires again, this is a deliberate per-visit
  // warning, not a per-browser one. Hides automatically when the
  // proposal has no costs yet.
  // ---- Excel export download with on-button loading feedback. The
  // 5-tab proposal workbook takes a few seconds to build server-side
  // (DOCX fetch + assumption rendering + cost-table compilation), so
  // we fetch the blob ourselves and stream it to the user with a
  // spinner instead of using a native <a download> (which gives no
  // visual feedback while the server is working).
  const [exportingXlsx, setExportingXlsx] = useState(false);
  // Has the client changed anything vs the canonical Medicys quote?
  // Drives the Excel export body so the backend knows to apply the
  // FX rate, replace pivots with the sandbox-adjusted ones, and emit
  // the red "illustrative-only" banner on every sheet.
  const currencyChanged = targetCurrency !== "GBP";
  const samplesChanged =
    Object.keys(sampleOverrides || {}).length > 0 ||
    Object.keys(unitOverrides || {}).length > 0;
  const modifications = [];
  if (currencyChanged) modifications.push("currency");
  if (samplesChanged) modifications.push("samples");
  const [xlsxError, setXlsxError] = useState("");
  const downloadExportXlsx = async () => {
    if (exportingXlsx || !exportUrl) return;
    setExportingXlsx(true);
    setXlsxError("");
    // Job flow (start → poll → download), mirrors the PDF export:
    // building the 6-tab workbook can outlive the proxy's ~100s
    // ceiling on a busy server; the job never holds a request open.
    const jobsBase = exportUrl.replace(/proposal\.xlsx$/, "proposal-xlsx-jobs");
    try {
      // POST the export-time params: current target currency / FX
      // rate, plus the adjusted pivots when the client has tweaked
      // samples. Backend defaults to the canonical Medicys quote
      // when fields are omitted.
      const body = {
        target_currency: targetCurrency,
        target_rate: Number(targetRate) || 1,
        modifications,
      };
      // Send the post-sandbox + post-ceil5 phases whenever the
      // client has touched ANYTHING (samples or currency), the
      // ceil5 incentive rounding lives in the React layer so a
      // GBP-only backend would otherwise miss it. Backend defaults
      // to the canonical Medicys quote when no phase_pivots come
      // in the body.
      if (
        (samplesChanged || currencyChanged) &&
        Array.isArray(adjusted)
      ) {
        body.phase_pivots = adjusted.map((a) => a.phase);
      }
      const startRes = await fetch(jobsBase, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!startRes.ok) {
        let msg = `HTTP ${startRes.status}`;
        try {
          const j = await startRes.json();
          if (j && j.detail) msg = j.detail;
        } catch {
          /* non-JSON */
        }
        throw new Error(msg);
      }
      const { job_id: jobId } = await startRes.json();
      let status = "rendering";
      let jobError = "";
      for (let i = 0; i < 96 && status === "rendering"; i += 1) {
        await new Promise((r) => setTimeout(r, 2500));
        const poll = await fetch(`${jobsBase}/${jobId}`);
        if (!poll.ok) continue;
        const j = await poll.json();
        status = j.status || "rendering";
        jobError = j.error || "";
      }
      if (status === "error") {
        throw new Error(jobError || "Excel export failed.");
      }
      if (status !== "done") {
        throw new Error(
          "The export is taking unusually long to prepare. Please try again in a minute."
        );
      }
      const res = await fetch(`${jobsBase}/${jobId}/download`);
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename =
        (m && m[1]) ||
        `proposal-${d.proposal_number || d.project_reference || "export"}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Tell the analytics pipeline the client just pulled the
      // proposal off-platform - feeds the engagement panel on /deals.
      if (typeof recordEvent === "function") {
        recordEvent("download", {
          meta: {
            format: "xlsx",
            // "medicys_export" distinguishes our auto-generated grid
            // (the rendered /proposal.xlsx) from a staff-uploaded
            // attachment ("client_grid"). The Engagement panel
            // surfaces these with different labels so deal owners
            // can tell which spreadsheet the client engaged with.
            kind: "medicys_export",
            // Note whether the download reflects sandbox-adjusted
            // pivots vs the canonical quote.
            ...(modifications.length
              ? { value: modifications.join(",") }
              : {}),
          },
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Excel export failed:", e);
      setXlsxError(
        String(e.message || e).slice(0, 220) ||
          "Excel export failed, please try again shortly."
      );
    } finally {
      setExportingXlsx(false);
    }
  };
  // ---- PDF export: the OFFICIAL quoted document (Cognito render,
  // cleaned + validity-stamped), GBP only on the client viewer.
  // Sample adjustments and currency conversion are the Excel's job -
  // converted on-screen figures use display rounding (nearest 5 in
  // target currency) that the PDF conversion can't mirror, so the
  // button is hidden while a non-GBP currency is active.
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const downloadExportPdf = async () => {
    if (exportingPdf || !pdfExportUrl) return;
    setExportingPdf(true);
    setPdfError("");
    // Job flow (start → poll → download): the render can take minutes
    // on a busy server, a single long request would hit the proxy's
    // ~100s ceiling (production 504s). Repeats are instant from cache.
    const jobsBase = pdfExportUrl.replace(/proposal\.pdf$/, "proposal-pdf-jobs");
    try {
      const startRes = await fetch(jobsBase, { method: "POST" });
      if (!startRes.ok) {
        let msg = `HTTP ${startRes.status}`;
        try {
          const j = await startRes.json();
          if (j && j.detail) msg = j.detail;
        } catch {
          /* non-JSON */
        }
        throw new Error(msg);
      }
      const { job_id: jobId } = await startRes.json();
      let status = "rendering";
      let jobError = "";
      for (let i = 0; i < 96 && status === "rendering"; i += 1) {
        await new Promise((r) => setTimeout(r, 2500));
        const poll = await fetch(`${jobsBase}/${jobId}`);
        if (!poll.ok) continue;
        const j = await poll.json();
        status = j.status || "rendering";
        jobError = j.error || "";
      }
      if (status === "error") {
        throw new Error(jobError || "PDF generation failed.");
      }
      if (status !== "done") {
        throw new Error(
          "The PDF is taking unusually long to prepare. Please try again in a minute - it will be ready instantly from cache."
        );
      }
      const res = await fetch(`${jobsBase}/${jobId}/download`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename =
        (m && m[1]) ||
        `proposal-${d.proposal_number || d.project_reference || "export"}.pdf`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      if (typeof recordEvent === "function") {
        recordEvent("download", {
          meta: {
            format: "pdf",
            kind: "medicys_export",
          },
        });
      }
    } catch (e) {
      setPdfError(
        String(e.message || e).slice(0, 220) ||
          "PDF download failed, please try again shortly."
      );
    } finally {
      setExportingPdf(false);
    }
  };
  const anyChanged = adjusted.some((a) => a.anyChanged);  const anyIncrease = adjusted.some((a) => a.anyIncrease);
  const adjustedGrand = adjusted.reduce(
    (acc, a) => acc + (a.phase.grand_total || 0),
    0
  );
  const quotedGrand = d.sub_total || 0;
  const delta = r2(adjustedGrand - quotedGrand);
  // 90-day quote validity countdown (per Medicys T&Cs).
  const validity = computeQuoteValidity(d.proposal_date);
  // Sample editing is opt-in: clients toggle "Adjust samples" first,
  // and must acknowledge a disclaimer before edit mode activates.
  // disclaimer: null | {type:"samples"} | {type:"currency", currency}
  //             | {type:"tab_open"}
  // Tab-open disclaimer auto-fires on first render when there are
  // costs to show and the client hasn't acknowledged yet, this
  // session.
  const [disclaimer, setDisclaimer] = useState(() =>
    !acknowledged && pivots.length > 0 ? { type: "tab_open" } : null
  );
  const toggleEditMode = () => {
    if (editMode) {
      resetAll(); // leaving edit mode restores quoted
      setEditMode(false);
    } else {
      setDisclaimer({ type: "samples" });
    }
  };
  const requestCurrency = (c) => {
    const previousCurrency = targetCurrency;
    if (c === "GBP") {
      setTargetCurrency("GBP");
      setTargetRate(1);
      if (typeof recordEvent === "function" && previousCurrency !== "GBP") {
        recordEvent("fx_switch", { meta: { from: previousCurrency, to: "GBP", rate: 1 } });
      }
    } else if (targetCurrency === "GBP") {
      // Moving away from GBP needs the invoicing disclaimer first.
      setDisclaimer({ type: "currency", currency: c });
    } else {
      setTargetCurrency(c);
      fetchRate(c);
      if (typeof recordEvent === "function") {
        recordEvent("fx_switch", { meta: { from: previousCurrency, to: c } });
      }
    }
  };
  const confirmDisclaimer = () => {
    if (disclaimer?.type === "samples") {
      setEditMode(true);
    } else if (disclaimer?.type === "currency") {
      const previousCurrency = targetCurrency;
      setTargetCurrency(disclaimer.currency);
      fetchRate(disclaimer.currency);
      if (typeof recordEvent === "function") {
        recordEvent("fx_switch", {
          meta: { from: previousCurrency, to: disclaimer.currency },
        });
      }
    } else if (disclaimer?.type === "tab_open") {
      setAcknowledged(true);
    }
    setDisclaimer(null);
  };

  // Live FX, pure ECB mid-market rate (no buffer / cover). Auto-runs
  // whenever the user picks a non-GBP currency; if it fails, the rate
  // input stays editable so they can type one in by hand.
  const [fetchingRate, setFetchingRate] = useState(false);
  const fetchRate = async (currency) => {
    if (!currency || currency === "GBP") return;
    setFetchingRate(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/fx/rate?to=${encodeURIComponent(
          currency
        )}`
      );
      if (res.ok) {
        const data = await res.json();
        if (data?.rate) setTargetRate(data.rate);
      }
    } catch {
      /* upstream blip, user can edit the rate manually */
    } finally {
      setFetchingRate(false);
    }
  };

  // Lookup maps that turn raw override keys into the human-readable
  // engagement meta the staff Activity feed shows ("Dermatologists
  // (UK): 10 → 8"). Built from the canonical (unadjusted) pivots.
  const engagementLabels = useMemo(() => {
    const blockQuoted = {};
    const blockLabel = {};
    const unitRow = {};
    (d.phase_pivots || []).forEach((phase, pi) => {
      (phase.sections || []).forEach((sec, si) => {
        let audience = null;
        (sec.rows || []).forEach((row, ri) => {
          if (row.kind === "subgroup_header") {
            audience = row.label || null;
            return;
          }
          if (row.kind === "count" && row.block_id != null) {
            if (blockLabel[row.block_id] == null) {
              blockLabel[row.block_id] =
                audience || row.label || sec.label || null;
            }
            const quoted = row.quoted_by_country || row.by_country || {};
            Object.entries(quoted).forEach(([c, q]) => {
              blockQuoted[`${pi}:${row.block_id}:${c}`] = q;
            });
            return;
          }
          if (row.per_unit && (row.quoted_units || row.units)) {
            unitRow[`${pi}:s${si}:r${ri}`] = {
              label: row.label || sec.label || null,
              quoted: row.quoted_units || row.units || {},
            };
          }
        });
      });
    });
    return { blockQuoted, blockLabel, unitRow };
  }, [d.phase_pivots]);

  const setOverride = (pi, bid, c, val) => {
    setSampleOverrides((prev) => {
      const key = `${pi}:${bid}:${c}`;
      const next = { ...prev };
      if (val == null) delete next[key];
      else next[key] = val;
      return next;
    });
    // Engagement signal: the client committed a sample change on
    // the Costs tab. `cleared=true` when the user reset back to the
    // canonical quoted sample. `label` + `previous` let the staff
    // activity feed read "Dermatologists (UK): 10 → 8".
    if (typeof recordEvent === "function") {
      const quoted = engagementLabels.blockQuoted[`${pi}:${bid}:${c}`];
      recordEvent("sample_change", {
        meta: {
          block_id: String(bid),
          label: engagementLabels.blockLabel[bid] || null,
          country: c,
          value: val == null ? null : Number(val),
          previous: quoted == null ? null : Number(quoted),
          cleared: val == null,
        },
      });
    }
  };

  const setUnitOverride = (pi, si, ri, c, val) => {
    setUnitOverrides((prev) => {
      const key = `${pi}:s${si}:r${ri}:${c}`;
      const next = { ...prev };
      if (val == null) delete next[key];
      else next[key] = val;
      return next;
    });
    // Engagement signal: service-unit edit (Moderation, Transcripts,
    // debriefs, etc.). Logged as a sample_change with kind:"unit" so
    // the staff feed can read "Moderation of 60-min WATDIs (UK): 10 → 8".
    if (typeof recordEvent === "function") {
      const meta = engagementLabels.unitRow[`${pi}:s${si}:r${ri}`];
      const quoted = meta?.quoted?.[c];
      recordEvent("sample_change", {
        meta: {
          block_id: `s${si}:r${ri}`,
          label: meta?.label || null,
          country: c,
          value: val == null ? null : Number(val),
          previous: quoted == null ? null : Number(quoted),
          cleared: val == null,
          kind: "unit",
        },
      });
    }
  };

  const resetAll = () => {
    setSampleOverrides({});
    setUnitOverrides({});
  };

  // Shared dialog markup, rendered in both the gated and unlocked
  // states so the modal can fire either before the costs are visible
  // (tab-open acknowledgment) or while they are (currency / samples).
  const dialog = (
    <AlertDialog
      open={disclaimer != null}
      onOpenChange={(open) => {
        if (!open) setDisclaimer(null);
      }}
    >
      <AlertDialogContent data-testid="disclaimer-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {disclaimer?.type === "currency"
              ? "A note on currency"
              : disclaimer?.type === "tab_open"
              ? "About these costs"
              : "Before you adjust sample sizes"}
          </AlertDialogTitle>
          {disclaimer?.type === "currency" ? (
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  The displayed rate is the live ECB mid-market GBP →{" "}
                  {disclaimer?.currency} rate, sourced from frankfurter.app
                  and refreshed every 12 hours. No buffer or FX cover is
                  applied to the conversion.
                </p>
                <p>
                  Converted figures are shown for convenience only. Unless
                  otherwise agreed, Medicys issues invoices in GBP (£).
                </p>
                <p>
                  Once you have switched currency, you can download a copy of
                  the converted figures using the &ldquo;Export in
                  Excel&rdquo; button above the cost tables.
                </p>
              </div>
            </AlertDialogDescription>
          ) : disclaimer?.type === "tab_open" ? (
            <AlertDialogDescription>
              These costs are based on the specifications you shared with us, together with our comments, assumptions, and proposed timings. Please review the Assumptions tab for the full scope on which these costs are based.
            </AlertDialogDescription>
          ) : (
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Adjusted figures are indicative only. If you increase any
                  sample size beyond the quoted scope, please check with the
                  Medicys team to confirm feasibility, and please note that
                  timelines are likely to be affected by changes to sample
                  sizes. The quoted proposal remains the binding offer.
                </p>
                <p>
                  Once you are happy with your adjusted sample sizes, you can
                  download a copy of the revised figures using the
                  &ldquo;Export in Excel&rdquo; button above the cost tables.
                </p>
              </div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {disclaimer?.type !== "tab_open" && (
            <AlertDialogCancel data-testid="disclaimer-cancel">
              Cancel
            </AlertDialogCancel>
          )}
          <AlertDialogAction
            onClick={confirmDisclaimer}
            data-testid="disclaimer-understand"
            className="bg-[#1B4F8A] hover:bg-[#173f6e]"
          >
            I understand
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // Gate: while the client hasn't acknowledged the tab-open
  // disclaimer, hide the cost figures. The modal opens
  // automatically; if dismissed via Esc, the "View costs" button
  // re-opens it so the user is never trapped.
  if (!acknowledged && pivots.length > 0) {
    return (
      <div className="space-y-10" data-testid="costs-tab">
        {dialog}
        <SectionHead
          n="03"
          title={d.phase_pivots?.length === 1 ? "Costs" : "Costs by phase"}
        />
        <div
          className="rounded-lg border border-stone-200 bg-stone-50 px-6 py-14 text-center"
          data-testid="costs-gate"
        >
          <Info className="w-7 h-7 mx-auto mb-4 text-[#1B4F8A]" aria-hidden />
          <button
            type="button"
            onClick={() => setDisclaimer({ type: "tab_open" })}
            data-testid="costs-gate-show"
            className="inline-flex items-center gap-2 rounded-full bg-[#1B4F8A] hover:bg-[#173f6e] text-white text-[11px] font-mono tracking-[0.14em] uppercase px-5 py-2.5 transition-colors"
          >
            View costs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="costs-tab">
      {dialog}
      {/* Supplier overlay banner, only renders when staff have
          flipped the overlay on AND winning bids exist. The numbers
          themselves aren't replaced here (yet); this banner is the
          visible confirmation that supplier-verified costs are in
          play, while the staff finalise the cell-level overrides. */}
      {supplierOverlay?.active &&
        Object.keys(supplierOverlay?.resolved || {}).length > 0 && (
          <div
            className="rounded-2xl border border-emerald-300/60 bg-emerald-50 px-4 py-3 flex items-start gap-3 text-emerald-900"
            data-testid="supplier-overlay-banner"
          >
            <div className="rounded-full bg-emerald-500/20 p-1.5 shrink-0">
              <Info className="w-3.5 h-3.5 text-emerald-700" />
            </div>
            <div className="text-xs leading-relaxed">
              <p className="font-medium">
                Costs verified with our delivery partners.
              </p>
              <p className="text-emerald-800/80 mt-0.5">
                {Object.keys(supplierOverlay.resolved).length} cost
                {Object.keys(supplierOverlay.resolved).length === 1 ? "" : "s"}{" "}
                in this quote{" "}
                {Object.keys(supplierOverlay.resolved).length === 1
                  ? "has"
                  : "have"}{" "}
                been confirmed by the suppliers who would deliver the work.
              </p>
            </div>
          </div>
        )}
      <div className="space-y-5">
        <SectionHead
          n="03"
          title={d.phase_pivots?.length === 1 ? "Costs" : "Costs by phase"}
        />
        <div className="flex items-center gap-3 flex-wrap">
          {pivots.length > 0 && exportUrl && (
            <button
              type="button"
              onClick={downloadExportXlsx}
              disabled={exportingXlsx}
              data-testid="export-proposal-xlsx"
              title={
                modifications.length === 0
                  ? "Full 5-tab proposal: Specifications, Assumptions, Costs (interactive with live formulas), Timelines and Terms"
                  : "Export your modified view (illustrative only, see the red banner inside the workbook)"
              }
              className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2.5 text-[11px] font-mono tracking-[0.14em] uppercase text-stone-600 hover:border-[#1B4F8A] hover:text-[#1B4F8A] transition-colors disabled:opacity-60 disabled:cursor-progress"
            >
              {exportingXlsx ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
              ) : (
                <Download className="w-3.5 h-3.5" aria-hidden />
              )}
              {exportingXlsx ? "Preparing…" : "Export in Excel"}
            </button>
          )}
          {pivots.length > 0 && pdfExportUrl && targetCurrency === "GBP" && (
            <button
              type="button"
              onClick={downloadExportPdf}
              disabled={exportingPdf}
              data-testid="export-proposal-pdf"
              title={
                samplesChanged
                  ? "The official quoted proposal document as a PDF. Your on-screen sample adjustments are not applied here - use the Excel export for those."
                  : "The official quoted proposal document as a PDF (GBP). For converted figures, use the Excel export."
              }
              className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2.5 text-[11px] font-mono tracking-[0.14em] uppercase text-stone-600 hover:border-[#1B4F8A] hover:text-[#1B4F8A] transition-colors disabled:opacity-60 disabled:cursor-progress"
            >
              {exportingPdf ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
              ) : (
                <Download className="w-3.5 h-3.5" aria-hidden />
              )}
              {exportingPdf ? "Preparing…" : "Download PDF"}
            </button>
          )}
          {pivots.length > 0 && (
            <button
              type="button"
              onClick={toggleEditMode}
              aria-pressed={editMode}
              data-testid="sample-edit-toggle"
              className={
                "inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-[11px] font-mono tracking-[0.14em] uppercase transition-colors " +
                (editMode
                  ? "bg-[#1B4F8A] border-[#1B4F8A] text-white hover:bg-[#173f6e]"
                  : "bg-white border-stone-300 text-stone-600 hover:border-[#1B4F8A] hover:text-[#1B4F8A]")
              }
            >
              <Pencil className="w-3.5 h-3.5" aria-hidden />
              {editMode ? "Adjusting samples" : "Adjust samples"}
            </button>
          )}
          <CurrencySwitcher
            theme="light"
            value={targetCurrency}
            rate={targetRate}
            onCurrencyChange={requestCurrency}
            loading={fetchingRate}
          />
        </div>
      </div>
      {xlsxError && (
        <p
          className="text-xs text-rose-600"
          role="alert"
          data-testid="xlsx-export-error"
        >
          {xlsxError}
        </p>
      )}
      {pdfError && (
        <p
          className="text-xs text-rose-600"
          role="alert"
          data-testid="pdf-export-error"
        >
          {pdfError}
        </p>
      )}
      {editMode && (
        <p className="text-xs text-stone-500" data-testid="sample-edit-hint">
          <Pencil className="w-3 h-3 inline mr-1 -mt-0.5" aria-hidden />
          Sample sizes and service units are editable: the shaded yellow
          cells are the ones you can adjust.
        </p>
      )}
      {anyChanged && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5"
          data-testid="adjusted-toolbar"
        >
          <p className="text-sm text-sky-900">
            Showing <strong>adjusted costs</strong> based on your changes.
          </p>
          <button
            type="button"
            onClick={resetAll}
            data-testid="reset-samples-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-900 border border-sky-300 rounded-full px-3 py-1.5 hover:bg-sky-100 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" aria-hidden />
            Reset to quoted
          </button>
        </div>
      )}
      {anyIncrease && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="alert"
          data-testid="feasibility-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" aria-hidden />
          <p>
            <strong>Adjustments above the quoted scope.</strong> These figures
            are indicative only. Please check with the Medicys team to confirm
            feasibility.
          </p>
        </div>
      )}
      {pivots.length > 0 ? (
        <div className="space-y-8" data-testid="phase-pivot">
          {pivots.map((p, pi) => (
            <PhasePivotCard
              key={pi}
              phase={adjusted[pi].phase}
              phaseIndex={pi}
              soloPhase={pivots.length === 1}
              money={money}
              recordEvent={recordEvent}
              targetCurrency={targetCurrency}
              targetRate={Number(targetRate) || 1}
              countryFx={d.country_fx || {}}
              getOverride={
                editMode
                  ? (bid, c) => sampleOverrides[`${pi}:${bid}:${c}`]
                  : undefined
              }
              onSampleChange={
                editMode ? (bid, c, val) => setOverride(pi, bid, c, val) : undefined
              }
              getUnitOverride={
                editMode
                  ? (si, ri, c) => unitOverrides[`${pi}:s${si}:r${ri}:${c}`]
                  : undefined
              }
              onUnitChange={
                editMode
                  ? (si, ri, c, val) => setUnitOverride(pi, si, ri, c, val)
                  : undefined
              }
            />
          ))}
          <div
            className={`bg-[#0B1220] text-white rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap ${
              validity?.expired ? "opacity-70" : ""
            }`}
            data-testid="grand-total-card"
          >
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#3EA8DC] font-medium">
                {anyChanged ? "Adjusted investment" : "Total investment"}
              </p>
              <p className="text-xs text-stone-300 mt-1">
                {anyChanged
                  ? "Indicative, based on your sample sizes · All figures exclusive of VAT"
                  : "All figures exclusive of VAT"}
              </p>
              {validity && !validity.expired && (
                <p
                  className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-wider text-[#3EA8DC]/95 rounded-full border border-[#3EA8DC]/35 bg-[#3EA8DC]/10 px-3 py-1"
                  data-testid="quote-validity-chip"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3EA8DC]" />
                  Valid until {fmtDate(validity.validUntil)}
                  {" · "}
                  {validity.expiresToday
                    ? "expires today"
                    : `${validity.daysRemaining} day${
                        validity.daysRemaining === 1 ? "" : "s"
                      } remaining`}
                </p>
              )}
              {validity?.expired && (
                <p
                  className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-wider text-stone-200 italic rounded-full border border-stone-600 bg-stone-800/60 px-3 py-1"
                  data-testid="quote-expired-banner"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-300" />
                  Quote expired on {fmtDate(validity.validUntil)}, please
                  request an updated proposal.
                </p>
              )}
            </div>
            <div className="text-right">
              <p
                className="text-3xl font-serif font-light"
                data-testid="proposal-grand-total"
              >
                {/* Sum of the displayed phase subtotals - NOT a one-shot
                    FX conversion of the Cognito quote - so this card
                    always equals what the columns above add up to,
                    penny for penny (matches the Excel export too). */}
                {money(pivots.length ? adjustedGrand : quotedGrand)}
              </p>
              {anyChanged && (
                <p
                  className="text-xs text-stone-300 mt-1"
                  data-testid="grand-total-delta"
                >
                  Quoted {money(quotedGrand)} ·{" "}
                  {delta >= 0 ? "+" : "−"}
                  {money(Math.abs(delta))}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-stone-500">No phase cost data.</p>
      )}
    </div>
  );
}

// Pivoted single-phase cost grid: countries-as-columns, cost-items-as-rows.
// Each amount cell shows the total prominently with a faint
// "per_unit × units" caption directly underneath so the client never
// has to ask "what made this number up?".
// Setup/Admin and study-management costs are never client-editable.
// they're scoped by Medicys, not by interview volume.
const UNITS_LOCKED_RE = /study\s*management|setup\s*\/?\s*admin/i;

function PhasePivotCard({ phase, phaseIndex, soloPhase, money, targetCurrency, targetRate, countryFx, getOverride, onSampleChange, getUnitOverride, onUnitChange, recordEvent }) {
  const countries = phase.countries || [];
  // Seven or more markets: ISO codes keep the header on one line.
  const compactHeads = countries.length > 6;
  const sections = phase.sections || [];
  const subtotals = phase.subtotal_by_country || {};
  // ALWAYS show the Total column, project-wide fees (weekly / monthly
  // study management) are allocated ONLY to Total, so hiding it (even
  // for single-country proposals) would drop those costs from view
  // entirely.
  const showTotal = true;
  const colCount = countries.length + 1 + (showTotal ? 1 : 0);
  // Active-display currency glyph, used by the ceil5 incentive caption
  // (which side-steps fmtMoney's mandatory 2-decimal format so clean
  // round amounts read as "$110 × 40" rather than "$110.00 × 40").
  const sym = SYMBOLS[targetCurrency] || "£";

  // Format the per-unit caption (e.g. "£180 × 60") or, for paired
  // Incentives-handling-fee rows, the per-country incentive base
  // amount (the rate is already shown under the row label).
  // Ceil5-rounded incentive rows in a non-GBP display drop the
  // trailing ".00" so the caption reads as a clean "$110 × 40"
  // (matches how staff would write it in a brief), the cell value
  // itself keeps the workbook-standard 2 decimal places.
  const captionFor = (row, c) => {
    if (row.kind !== "amount") return null;
    if (row.caption_rate != null && row.caption_base?.[c] != null) {
      return money(row.caption_base[c]);
    }
    const pu = row.per_unit?.[c];
    const u = row.units?.[c];
    if (!pu || !u) return null;
    if (row._ceil5_target) {
      const target = Math.round(Number(pu) * Number(targetRate));
      return `${sym}${target.toLocaleString("en-GB")} × ${Number(u).toLocaleString("en-GB")}`;
    }
    return `${money(pu)} × ${Number(u).toLocaleString("en-GB")}`;
  };

  // ---- Foreign-currency incentive helpers ----
  // Surface the on-the-ground participant payout next to every
  // Incentives cell whenever the local market currency DIFFERS from
  // the currency the viewer is currently displaying. A USD-quoted
  // proposal still shows "or £X each" under the UK column; a
  // GBP-quoted proposal shows "or $X each" under the US column; a
  // USD-quoted US column shows nothing (the cell is already in USD).
  // Non-GBP local payouts are floor5'd in the country's native
  // currency, matching the incentive cell direction (round DOWN);
  // GBP-native payouts pass through exact (the canonical quote is
  // already in pounds). ``isGbpNative`` lets the caller hide the
  // "subject to FX" tooltip, which doesn't apply to GBP payouts
  // (they settle in GBP).
  const floor5Local = (n) => Math.floor(Number(n) / 5) * 5;
  const isIncentiveRow = (row) =>
    row?.kind === "amount" &&
    row.caption_rate == null &&
    /incentive/i.test(row.label || "");
  const localIncentiveFor = (pu, country) => {
    if (!pu) return null;
    const fx = countryFx?.[country];
    if (!fx || !fx.currency || !fx.rate) return null;
    if (fx.currency === targetCurrency) return null;
    const raw = Number(pu) * Number(fx.rate);
    const isGbpNative = fx.currency === "GBP";
    return {
      symbol: fx.symbol || fx.currency + " ",
      localAmount: isGbpNative ? raw : floor5Local(raw),
      isGbpNative,
    };
  };

  return (
    <div
      className="bg-white border border-stone-200 rounded-xl [overflow:clip]"
      data-testid={`phase-pivot-card-${phaseIndex}`}
    >
      {/* With a single phase the header strip is pure noise, the tab
          is already titled "Costs by phase" and there's nothing to
          disambiguate, so hide it entirely. */}
      {!soloPhase && (
        <div className="bg-stone-100/60 px-5 py-3 border-b border-stone-200">
          <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1 font-medium">
            Phase {String(phaseIndex + 1).padStart(2, "0")}
          </p>
          <p className="font-medium text-stone-900">{phase.phase_name || "-"}</p>
        </div>
      )}
      <div className="overflow-x-auto md:overflow-x-visible">
        <table
          className={
            "w-full font-aptos-mono table-fixed " +
            (compactHeads
              ? "text-[11px] [&_td]:px-2 [&_th]:px-2 [&_th]:text-xs"
              : "text-xs")
          }
        >
          <colgroup>
            <col className={compactHeads ? "w-44 sm:w-[200px]" : "w-44 sm:w-[260px]"} />
            {countries.map((c) => (
              <col key={c} className="w-28 sm:w-auto" />
            ))}
            {showTotal && <col className={compactHeads ? "w-28 sm:w-[104px]" : "w-28 sm:w-auto"} />}
          </colgroup>
          <thead>
            {/* Sticky table header. ``top-[68px]`` clears the sticky
                tab bar above (px-5 py-3 wrapper + py-2.5 text-base
                tab buttons ≈ 68px). The header stays anchored at the
                viewport top as the user scrolls so they never lose
                track of which country a cost cell belongs to.        */}
            <tr className="text-stone-900 bg-stone-50 shadow-[0_1px_0_0_rgba(0,0,0,0.06)] sticky top-[68px] z-10">
              <th
                className="px-3 py-3 text-sm font-bold tracking-[0.12em] uppercase text-left sticky left-0 z-20 bg-stone-50"
              >
                Cost item
              </th>
              {countries.map((c, ci) => (
                <th
                  key={c}
                  title={compactHeads ? c : undefined}
                  data-testid={`pivot-country-head-${phaseIndex}-${ci}`}
                  className="px-3 py-3 text-sm font-bold tracking-[0.12em] uppercase text-right"
                >
                  {compactHeads ? isoCountryCode(c) : c}
                </th>
              ))}
              {showTotal && (
                <th className="px-3 py-3 text-sm font-bold tracking-[0.12em] uppercase text-right bg-stone-100">
                  Total
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Flat render pipeline so we can interleave section
              // banners (Services / Setup & admin) and the existing
              // rows in one pass.
              const out = [];
              let audienceIdx = -1;
              let headerIdx = -1;

              const renderRow = (row, key, si, ri, secLabel) => {
                const isCount = row.kind === "count";
                const unitsLocked =
                  secLabel === "Setup & admin" ||
                  UNITS_LOCKED_RE.test(row.label || "");
                // Pre-compute the per-country local-currency
                // payouts so we know whether to emit the follow-up
                // "in local currency" row beneath an incentive row.
                // The row mirrors the parent's caption format -
                // currency sign + per-unit × units - once per
                // country that has a non-display-currency native
                // currency. The FX-subject ⓘ tooltip only appears
                // when at least one country has a non-GBP-native
                // payout (GBP payouts settle in GBP exactly).
                const localPerCountry = isIncentiveRow(row)
                  ? Object.fromEntries(
                      countries.map((c) => {
                        const sourcePu =
                          row._quoted_per_unit?.[c] ??
                          row.per_unit?.[c];
                        return [c, localIncentiveFor(sourcePu, c)];
                      })
                    )
                  : null;
                const showLocalRow =
                  localPerCountry &&
                  Object.values(localPerCountry).some(Boolean);
                const localHasFxRisk =
                  showLocalRow &&
                  Object.values(localPerCountry).some(
                    (l) => l && !l.isGbpNative
                  );
                return (
                  <Fragment key={key}>
                  <tr
                    className={
                      isCount
                        ? "border-t border-stone-100 bg-sky-50 align-top"
                        : "border-t border-stone-100 align-top"
                    }
                  >
                    <td
                      className={
                        isCount
                          ? "px-3 py-2.5 text-stone-900 font-medium break-words sticky left-0 z-10 bg-sky-50"
                          : "px-3 py-2.5 text-stone-900 break-words sticky left-0 z-10 bg-white"
                      }
                    >
                      <CostRowLabel
                        label={row.label}
                        onLinkClick={(info) => {
                          if (typeof recordEvent === "function") {
                            recordEvent("framework_click", { meta: info });
                          }
                        }}
                      />
                      {isCount && row.block_id && onSampleChange && (
                        <Pencil
                          className="w-3 h-3 inline ml-1.5 -mt-0.5 text-stone-400"
                          aria-hidden
                        />
                      )}
                      {row.label_note && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex align-middle ml-1.5 -mt-0.5 text-red-600 hover:text-red-700 cursor-help"
                                aria-label={row.label_note}
                                data-testid={`label-note-${key}`}
                              >
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[260px] text-xs">
                              {row.label_note}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {row.subtitle && (
                        <div className="text-[9px] text-stone-500 mt-0.5 font-normal">
                          {row.subtitle}
                        </div>
                      )}
                    </td>
                    {row._global && !isCount && countries.length > 0 ? (
                      <td
                        colSpan={countries.length}
                        className="px-3 py-2.5 text-right text-[10px] italic text-stone-400"
                        data-testid={`project-wide-cell-${key}`}
                      >
                        Project-wide · not split by market
                      </td>
                    ) : countries.map((c) => {
                      const v = row.by_country?.[c];
                      const caption = captionFor(row, c);
                      if (isCount) {
                        const quoted = row.quoted_by_country
                          ? row.quoted_by_country[c]
                          : v;
                        if (row.block_id && quoted > 0 && onSampleChange) {
                          const raw = getOverride?.(row.block_id, c);
                          const changed = Number(v) !== Number(quoted);
                          return (
                            <td
                              key={c}
                              className={
                                "px-2 py-1.5 text-right " +
                                (changed ? "bg-amber-100/60" : "bg-[#FFFBEA]")
                              }
                            >
                              <input
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                value={raw != null ? raw : String(v ?? "")}
                                onChange={(e) =>
                                  onSampleChange(row.block_id, c, e.target.value)
                                }
                                onBlur={(e) => {
                                  if (
                                    e.target.value === "" ||
                                    Number(e.target.value) === Number(quoted)
                                  ) {
                                    onSampleChange(row.block_id, c, null);
                                  }
                                }}
                                aria-label={`Sample size for ${c}`}
                                data-testid={`sample-input-${phaseIndex}-${row.block_id}-${c}`}
                                className={
                                  "w-16 max-w-full text-right text-xs font-aptos-mono rounded border px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#1B4F8A] transition-colors " +
                                  (changed
                                    ? "border-amber-400 text-amber-900 font-semibold"
                                    : "border-stone-300 text-stone-900")
                                }
                              />
                              {changed && (
                                <div className="text-[9px] text-stone-400 mt-0.5">
                                  quoted {Number(quoted).toLocaleString("en-GB")}
                                </div>
                              )}
                            </td>
                          );
                        }
                        const per = row.derived_units_per?.[c];
                        const quotedDerived = row.quoted_by_country?.[c];
                        const derivedChanged =
                          quotedDerived != null && Number(v) !== Number(quotedDerived);
                        return (
                          <td
                            key={c}
                            className={
                              "px-3 py-2.5 text-right text-stone-900" +
                              (derivedChanged ? " bg-amber-100/60 font-semibold text-amber-900" : "")
                            }
                            data-testid={row.derived_from ? `derived-count-${phaseIndex}-${row.derived_from}-${c}` : undefined}
                          >
                            {v != null ? Number(v).toLocaleString("en-GB") : "-"}
                            {per != null && (
                              <div className="text-[9px] text-stone-400 mt-0.5 font-normal">
                                {Number(per).toLocaleString("en-GB")} per respondent
                                {derivedChanged && ` · quoted ${Number(quotedDerived).toLocaleString("en-GB")}`}
                              </div>
                            )}
                          </td>
                        );
                      }
                      if (row._global) {
                        return (
                          <td
                            key={c}
                            className="px-3 py-2.5 text-right text-stone-400"
                          >
                            -
                          </td>
                        );
                      }
                      // Editable service/setup units: rows without a
                      // block_id whose cell has a per-unit rate. The
                      // amount renders above a "rate × [units]" input
                      // caption; editable cells get a soft yellow
                      // tint so clients can see what's adjustable.
                      const pu = row.per_unit?.[c];
                      const quotedU = row.quoted_units
                        ? row.quoted_units[c]
                        : row.units?.[c];
                      const unitEditable =
                        onUnitChange &&
                        !row.block_id &&
                        !unitsLocked &&
                        pu &&
                        quotedU > 0;
                      const u = row.units?.[c];
                      const unitChanged =
                        unitEditable && Number(u ?? 0) !== Number(quotedU);
                      return (
                        <td
                          key={c}
                          className={
                            "px-3 py-2.5 text-right text-stone-900 " +
                            (unitEditable
                              ? unitChanged
                                ? "bg-amber-100/60"
                                : "bg-[#FFFBEA]"
                              : "")
                          }
                        >
                          {(() => {
                            if (unitEditable) {
                              const raw = getUnitOverride?.(si, ri, c);
                              const changed = unitChanged;
                              return (
                                <>
                                  <div>{v ? money(v) : money(0)}</div>
                                  <div className="flex items-center justify-end gap-1 text-[9px] text-stone-400 mt-0.5">
                                    <span className="whitespace-nowrap">
                                      {money(pu)} ×
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="any"
                                      inputMode="decimal"
                                      value={raw != null ? raw : String(u ?? "")}
                                      onChange={(e) =>
                                        onUnitChange(si, ri, c, e.target.value)
                                      }
                                      onBlur={(e) => {
                                        // Clear the pin only when the typed
                                        // value matches the sample-scaled
                                        // baseline (what the cell shows with
                                        // no override), NOT the quoted value:
                                        // otherwise typing the quoted number
                                        // back while a sample change is
                                        // active snaps the cell to the
                                        // scaled figure.
                                        const baselineU =
                                          row.baseline_units?.[c] ?? quotedU;
                                        if (
                                          e.target.value === "" ||
                                          Number(e.target.value) ===
                                            Number(baselineU)
                                        ) {
                                          onUnitChange(si, ri, c, null);
                                        }
                                      }}
                                      aria-label={`Units for ${row.label} (${c})`}
                                      data-testid={`unit-input-${phaseIndex}-s${si}-r${ri}-${c}`}
                                      className={
                                        "w-14 max-w-full text-right text-[10px] font-aptos-mono rounded border px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#1B4F8A] transition-colors " +
                                        (changed
                                          ? "border-amber-400 text-amber-900 font-semibold"
                                          : "border-stone-300 text-stone-700")
                                      }
                                    />
                                  </div>
                                  {changed && (
                                    <div className="text-[9px] text-amber-600 mt-0.5">
                                      quoted {Number(quotedU).toLocaleString("en-GB")}
                                    </div>
                                  )}
                                </>
                              );
                            }
                            return (
                              <>
                                {v ? (
                                  money(v)
                                ) : (
                                  <span className="text-stone-400">-</span>
                                )}
                                {caption && (
                                  <div className="text-[9px] text-stone-400 mt-0.5">
                                    {caption}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      );
                    })}
                    {showTotal &&
                      (() => {
                        const quotedGU =
                          row.quoted_global_units ?? row.global_units;
                        const globalEditable =
                          row._global &&
                          row.global_per_unit &&
                          onUnitChange &&
                          !row.block_id &&
                          !unitsLocked &&
                          quotedGU > 0;
                        const globalChanged =
                          globalEditable &&
                          Number(row.global_units ?? 0) !== Number(quotedGU);
                        return (
                          <td
                            className={
                              "px-3 py-2.5 text-right text-stone-900 " +
                              (globalEditable
                                ? globalChanged
                                  ? "bg-amber-100/60"
                                  : "bg-[#FFFBEA]"
                                : "bg-stone-50/60")
                            }
                          >
                        {isCount
                          ? Number(row.total || 0).toLocaleString("en-GB")
                          : money(row.total)}
                        {row._global &&
                          row.global_per_unit &&
                          (() => {
                            if (globalEditable) {
                              const raw = getUnitOverride?.(si, ri, "total");
                              const changed = globalChanged;
                              return (
                                <>
                                  <div className="flex items-center justify-end gap-1 text-[9px] text-stone-400 mt-0.5 font-normal">
                                    <span className="whitespace-nowrap">
                                      {money(row.global_per_unit)} ×
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="any"
                                      inputMode="decimal"
                                      value={
                                        raw != null
                                          ? raw
                                          : String(row.global_units ?? "")
                                      }
                                      onChange={(e) =>
                                        onUnitChange(si, ri, "total", e.target.value)
                                      }
                                      onBlur={(e) => {
                                        if (
                                          e.target.value === "" ||
                                          Number(e.target.value) ===
                                            Number(quotedGU)
                                        ) {
                                          onUnitChange(si, ri, "total", null);
                                        }
                                      }}
                                      aria-label={`Units for ${row.label}`}
                                      data-testid={`unit-input-${phaseIndex}-s${si}-r${ri}-total`}
                                      className={
                                        "w-14 max-w-full text-right text-[10px] font-aptos-mono rounded border px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#1B4F8A] transition-colors " +
                                        (changed
                                          ? "border-amber-400 text-amber-900 font-semibold"
                                          : "border-stone-300 text-stone-700")
                                      }
                                    />
                                  </div>
                                  {changed && (
                                    <div className="text-[9px] text-amber-600 mt-0.5 font-normal">
                                      quoted {Number(quotedGU).toLocaleString("en-GB")}
                                    </div>
                                  )}
                                </>
                              );
                            }
                            return row.global_units ? (
                              <div className="text-[9px] text-stone-400 mt-0.5 font-normal">
                                {money(row.global_per_unit)} ×{" "}
                                {Number(row.global_units).toLocaleString("en-GB")}
                              </div>
                            ) : null;
                          })()}
                        {row.caption_rate != null && row.caption_base_total != null && (
                          <div className="text-[9px] text-stone-400 mt-0.5 font-normal">
                            {money(row.caption_base_total)}
                          </div>
                        )}
                          </td>
                        );
                      })()}
                  </tr>
                  {/* Follow-up "in local currency" row. Sits directly
                      below an incentive row, indented in the cost-
                      item column and gray-styled like a caption.
                      Each country cell mirrors the parent row's
                      caption format (currency sign + per-unit ×
                      units) using the on-the-ground native payout,
                      so a reader can spot the matching pair on the
                      same horizontal axis. The FX-subject ⓘ icon
                      appears once per row, only when at least one
                      country has a non-GBP-native payout (GBP
                      payouts settle in GBP exactly, no FX risk). */}
                  {showLocalRow && (
                    <tr
                      data-testid={`local-currency-row-${key}`}
                      className="border-t-0 align-top"
                    >
                      <td className="px-3 pt-0 pb-2 sticky left-0 z-10 bg-white">
                        <div
                          className="text-[9px] text-stone-400 font-normal ml-3 flex items-center gap-1"
                          data-testid={`local-currency-sublabel-${phaseIndex}`}
                        >
                          <span>in local currency</span>
                          {localHasFxRisk && (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Foreign-currency incentive note"
                                    data-testid={`incentive-fx-info-${phaseIndex}`}
                                    className="text-[#B91C1C] hover:text-[#7F1D1D] focus:outline-none"
                                  >
                                    <Info className="w-3 h-3" aria-hidden />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-[11px] leading-relaxed">
                                  The local-currency incentive amount is
                                  subject to change depending on the FX
                                  rate at the time of commissioning.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </td>
                      {countries.map((c) => {
                        const local = localPerCountry?.[c];
                        const u = row.units?.[c];
                        return (
                          <td
                            key={c}
                            className="px-3 pt-0 pb-2 text-right text-[9px] text-stone-400"
                          >
                            {local && u
                              ? `${local.symbol}${local.localAmount.toLocaleString(
                                  "en-GB",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )} × ${Number(u).toLocaleString("en-GB")}`
                              : null}
                          </td>
                        );
                      })}
                      {showTotal && (
                        <td className="px-3 pt-0 pb-2 bg-stone-50/60" />
                      )}
                    </tr>
                  )}
                  </Fragment>
                );
              };

              sections.forEach((sec, si) => {
                const isAudienceSection =
                  sec.label === "Recruitment, incentives & PM";

                // Section banner for non-audience sections (Services,
                // Setup & admin, fallback handling fee).
                if (!isAudienceSection) {
                  out.push(
                    <tr
                      key={`s${si}-banner`}
                      data-testid={`section-banner-${si}`}
                      className="border-t-[6px] border-stone-100 bg-stone-100/70"
                    >
                      <td colSpan={colCount} className="px-4 py-2">
                        <p className="text-[10px] tracking-[0.22em] uppercase text-stone-600 font-semibold">
                          {sec.label}
                        </p>
                      </td>
                    </tr>
                  );
                }

                sec.rows.forEach((row, ri) => {
                  const key = `s${si}-r${ri}`;
                  if (row.kind === "subgroup_header") {
                    headerIdx += 1;
                    // Methodology-split continuation blocks belong to
                    // the same audience, don't advance the counter.
                    if (!row.continuation) audienceIdx += 1;
                    const isFirst = headerIdx === 0;
                    out.push(
                      <tr
                        key={key}
                        data-testid={`subgroup-header-${headerIdx}`}
                        className={
                          isFirst
                            ? "bg-stone-50/60"
                            : "bg-stone-50/60 border-t-[8px] border-stone-100"
                        }
                      >
                        <td colSpan={colCount} className="p-0">
                          <div className="flex items-stretch">
                            <div
                              className="w-1.5 shrink-0"
                              style={{ backgroundColor: "#1B4F8A" }}
                              aria-hidden
                            />
                            <div className="flex-1 px-4 py-3">
                              <div className="min-w-0">
                                {!row.solo && !row.continuation && (
                                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#1B4F8A] font-semibold mb-1">
                                    Audience {String(audienceIdx + 1).padStart(2, "0")}
                                  </p>
                                )}
                                <p className="text-base font-semibold text-stone-900 leading-snug">
                                  {row.label}
                                </p>
                                {row.subtitle && (
                                  <span
                                    className="inline-flex max-w-full items-center rounded-xl bg-[#1B4F8A]/10 mt-2 px-3 py-1 text-[11px] font-medium tracking-wide text-[#1B4F8A] text-left leading-snug"
                                    data-testid={`methodology-chip-${headerIdx}`}
                                  >
                                    {row.subtitle}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                    return;
                  }
                  out.push(renderRow(row, key, si, ri, sec.label));
                });
              });

              return out;
            })()}
            {/* Subtotal row */}
            <tr className="border-t-2 border-stone-300 bg-stone-50 font-medium">
              <td className="px-3 py-2.5 text-stone-900 sticky left-0 z-10 bg-stone-50">
                {soloPhase ? "Subtotal" : "Phase subtotal"}
              </td>
              {countries.map((c) => (
                <td
                  key={c}
                  className="px-3 py-2.5 text-right text-stone-900"
                >
                  {subtotals[c] != null ? money(subtotals[c]) : "-"}
                </td>
              ))}
              {showTotal && (
                <td className="px-3 py-2.5 text-right text-stone-900 bg-stone-100">
                  {money(phase.grand_total)}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
